// src/utils/axiosConfig.js
// UNIFIED AXIOS CONFIG - With 401 interceptor and singleton refresh
import axios from 'axios';
import { TokenService } from './TokenService';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  withCredentials: true // Include credentials for CORS (httpOnly refresh cookie)
});

// Singleton refresh promise - prevents multiple simultaneous refreshes
let refreshPromise = null;

/**
 * Get access token via TokenService
 */
const getAccessToken = () => {
  return TokenService.getAccessToken();
};

/**
 * Singleton refresh function - queues multiple calls
 * Uses httpOnly cookie for refresh token
 */
const getRefreshedTokenOnce = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        console.log('🔄 [AXIOS] Attempting token refresh...');

        const response = await axios.get(`${API_URL}/refresh`, {
          withCredentials: true,
          timeout: 10000
        });

        if (response.data?.accessToken) {
          const { accessToken, user } = response.data;

          // Update via TokenService
          TokenService.setAccessToken(accessToken);
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }

          // Update axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          console.log('✅ [AXIOS] Token refreshed successfully');
          return accessToken;
        }

        throw new Error('Invalid refresh response');
      } catch (error) {
        console.error('❌ [AXIOS] Token refresh failed:', error.response?.status || error.message);
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
};

/**
 * REQUEST INTERCEPTOR
 * Attaches authorization token to all requests
 */
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = getAccessToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    console.log('📤 API Request:', config.method?.toUpperCase(), config.url);

    return config;
  },
  (error) => {
    console.error('📤 Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR
 * Handles 401 errors with automatic token refresh and request replay
 */
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.method?.toUpperCase(), response.config.url, response.status);
    return response;
  },
  async (error) => {
    const { config, response } = error;

    // If no response, it's a network error
    if (!response) {
      if (!axios.isCancel(error)) {
        console.error('🌐 Network error:', error.message);
      }
      return Promise.reject(error);
    }

    const status = response.status;
    const url = config?.url;

    console.log(`❌ API Error ${status} for ${config?.method?.toUpperCase()} ${url}`);

    // Handle 401 Unauthorized - Token expired
    if (status === 401 && config && !config.__isRetry) {
      console.log('🔐 [AXIOS] 401 Unauthorized - attempting refresh...');

      try {
        // Attempt to refresh token (singleton - queues multiple calls)
        const newToken = await getRefreshedTokenOnce();

        // Mark this request as retry to prevent infinite loops
        config.__isRetry = true;

        // Update request header with new token
        config.headers.Authorization = `Bearer ${newToken}`;

        console.log('🔄 [AXIOS] Retrying request with new token:', config.method?.toUpperCase(), config.url);

        // Retry the original request
        return api(config);
      } catch (refreshError) {
        console.error('❌ [AXIOS] Refresh failed, clearing auth');

        // Check if user is a guest
        const userStr = localStorage.getItem('user');
        let isGuest = false;

        try {
          const user = userStr ? JSON.parse(userStr) : null;
          isGuest = user?.role === 'guest';
        } catch (e) {
          // Ignore parse errors
        }

        // Clear auth state
        TokenService.clearAccessToken();
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        delete api.defaults.headers.common['Authorization'];

        // Save return URL for authenticated users (not guests)
        if (!isGuest) {
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/login' && !currentPath.includes('/join-tour')) {
            localStorage.setItem('returnUrl', currentPath);
            console.log('📍 [AXIOS] Saved returnUrl:', currentPath);
          }
        }

        // Dispatch auth-error event for components to handle navigation
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { status: 401, url: url, isGuest }
        }));

        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.warn('⚠️ 403 Forbidden:', url);

      // For participant endpoints, return empty data instead of throwing
      if (url?.includes('/participants') || url?.includes('/participant/')) {
        console.warn('⚠️ [AXIOS] 403 Forbidden accessing participants - returning empty data');
        return Promise.resolve({
          data: {
            success: false,
            participants: [],
            tour: null,
            messages: [],
            message: 'Access denied to participants data'
          },
          status: 200,
          config: error.config
        });
      }
    }

    // Handle 404 Not Found
    if (status === 404) {
      console.warn('🔍 404 Not Found:', url);
    }

    // Handle 500 Server Error
    if (status === 500) {
      console.error('🔥 500 Server Error:', url);
    }

    // Log cancelled requests
    if (axios.isCancel(error)) {
      console.log('🚫 Request cancelled:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to check if API is configured correctly
 */
api.checkConfiguration = () => {
  console.log('🔧 Axios Configuration Check:');
  console.log('- Base URL:', API_URL);
  console.log('- Timeout:', api.defaults.timeout);
  console.log('- With Credentials:', api.defaults.withCredentials);
  console.log('- Auth Token Present:', !!getAccessToken());

  return {
    baseURL: API_URL,
    hasToken: !!getAccessToken(),
    isConfigured: !!API_URL
  };
};

/**
 * Enhanced helper for participant-specific requests that might fail
 */
api.safeGet = async (url, config = {}) => {
  try {
    const response = await api.get(url, config);
    return response;
  } catch (error) {
    if (error.response?.status === 403) {
      console.warn(`⚠️ [AXIOS] 403 Forbidden for ${url} - returning empty data`);
      return {
        data: {
          success: false,
          participants: [],
          tour: null,
          messages: [],
          message: 'Access denied'
        },
        status: 200,
        config: error.config
      };
    }

    throw error;
  }
};

/**
 * Add retry mechanism for critical requests
 */
api.retryGet = async (url, config = {}, maxRetries = 3) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 [AXIOS] Attempt ${attempt}/${maxRetries} for ${url}`);
      const response = await api.get(url, config);
      return response;
    } catch (error) {
      lastError = error;

      // Don't retry on 403/404 errors
      if (error.response?.status === 403 || error.response?.status === 404) {
        throw error;
      }

      // Wait before retry (exponential backoff)
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
        console.log(`⏳ [AXIOS] Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

export default api;
